import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const toggleLanguage = () => {
    const next = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(next);
  };

  const isEs = i18n.language === 'es';

  const links = [
    { labelKey: 'nav.benefits', id: 'benefits' },
    { labelKey: 'nav.mission', id: 'emotional' },
    { labelKey: 'nav.contact', id: 'contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-brand-black/95 backdrop-blur-sm border-b border-gold/20' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/8348ef887df579edc93e4302d73f13a8.png"
            alt="AdoraLED Logo"
            className="h-8 md:h-10 w-auto object-contain"
          />
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="font-body text-sm text-brand-white/80 hover:text-gold transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {t(link.labelKey)}
            </button>
          ))}

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="font-body text-xs font-semibold border border-brand-white/30 text-brand-white/70 hover:border-gold/60 hover:text-gold px-3 py-1.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            aria-label="Toggle language"
          >
            {isEs ? 'EN' : 'ES'}
          </button>

          <button
            onClick={() => scrollTo('contact')}
            className="font-body text-sm font-semibold bg-gold text-brand-black px-5 py-2 rounded-full hover:bg-gold-light transition-colors duration-300 cursor-pointer whitespace-nowrap animate-glow-pulse"
          >
            {t('nav.cta')}
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-8 h-8 text-brand-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-brand-black/98 border-t border-gold/20 px-4 py-6 flex flex-col gap-4">
          {links.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="font-body text-base text-brand-white/80 hover:text-gold text-left cursor-pointer"
            >
              {t(link.labelKey)}
            </button>
          ))}

          {/* Language Toggle Mobile */}
          <button
            onClick={toggleLanguage}
            className="font-body text-sm font-semibold border border-brand-white/30 text-brand-white/70 hover:border-gold/60 hover:text-gold px-4 py-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap w-fit"
          >
            {isEs ? 'Switch to English' : 'Cambiar a Español'}
          </button>

          <button
            onClick={() => scrollTo('contact')}
            className="font-body text-sm font-semibold bg-gold text-brand-black px-5 py-3 rounded-full hover:bg-gold-light transition-colors duration-300 cursor-pointer mt-2 whitespace-nowrap"
          >
            {t('nav.cta')}
          </button>
        </div>
      )}
    </nav>
  );
}
